import { am, factory } from './utils';

import httpClient from '@/utils/httpClient';
import logger from '@/utils/logger';

const GET_SETTINGS = am(`GET_SETTINGS`);
const SCHEDULE_MANUALLY = am(`SCHEDULE_MANUALLY`);
const ACTIVATE = am(`ACTIVATE`);
const DEACTIVATE = am(`DEACTIVATE`);
const FETCH_HISTORY = am(`FETCH_HISTORY`);

// id alias so the shared factory's id-based matching (store/utils.js) and common <List> row keys work for Mongo documents
const withId = doc => (doc ? { ...doc, id: doc._id } : doc);

// guards fetchHistory against out-of-order responses when pagination is clicked quickly
let historyRequestToken = 0;

const setActive = (state, { pipelineId, isActive }) => {
  if (state.item && state.item._id === pipelineId) {
    state.item.isActive = isActive;
  }
  const entry = state.collection.find(t => t._id === pipelineId);
  if (entry) {
    entry.isActive = isActive;
  }
};

export default {
  namespaced: true,
  ...factory({
    state: {
      isSettingsRequestPending: false,
      isSettingsRequestFailed: false,
      isScheduleManuallyRequestPending: false,
      isScheduleManuallyRequestFailed: false,
      settings: null,
      isActivateRequestPending: false,
      isActivateRequestFailed: false,
      isFetchingHistory: false,
      isFetchingHistoryFailed: false,
      history: { data: [], total: 0 }
    },
    mutations: {
      [GET_SETTINGS.STARTED](state) {
        state.isSettingsRequestPending = true;
        state.isSettingsRequestFailed = false;
      },
      [GET_SETTINGS.COMPLETED](state, settings) {
        state.isSettingsRequestPending = false;
        state.settings = settings;
      },
      [GET_SETTINGS.FAILED](state) {
        state.isSettingsRequestPending = false;
        state.isSettingsRequestFailed = true;
      },
      [SCHEDULE_MANUALLY.STARTED](state) {
        state.isScheduleManuallyRequestPending = true;
        state.isScheduleManuallyRequestFailed = false;
      },
      [SCHEDULE_MANUALLY.COMPLETED](state) {
        state.isScheduleManuallyRequestPending = false;
      },
      [SCHEDULE_MANUALLY.FAILED](state) {
        state.isScheduleManuallyRequestPending = false;
        state.isScheduleManuallyRequestFailed = true;
      },
      [ACTIVATE.STARTED](state) {
        state.isActivateRequestPending = true;
        state.isActivateRequestFailed = false;
      },
      [ACTIVATE.COMPLETED](state, { pipelineId }) {
        state.isActivateRequestPending = false;
        setActive(state, { pipelineId, isActive: true });
      },
      [ACTIVATE.FAILED](state) {
        state.isActivateRequestPending = false;
        state.isActivateRequestFailed = true;
      },
      [DEACTIVATE.STARTED](state) {
        state.isActivateRequestPending = true;
        state.isActivateRequestFailed = false;
      },
      [DEACTIVATE.COMPLETED](state, { pipelineId }) {
        state.isActivateRequestPending = false;
        setActive(state, { pipelineId, isActive: false });
      },
      [DEACTIVATE.FAILED](state) {
        state.isActivateRequestPending = false;
        state.isActivateRequestFailed = true;
      },
      [FETCH_HISTORY.STARTED](state) {
        state.isFetchingHistory = true;
        state.isFetchingHistoryFailed = false;
      },
      [FETCH_HISTORY.COMPLETED](state, { data, total }) {
        state.isFetchingHistory = false;
        state.history = { data, total };
      },
      [FETCH_HISTORY.FAILED](state) {
        state.isFetchingHistory = false;
        state.isFetchingHistoryFailed = true;
      },
      RESET_HISTORY(state) {
        state.history = { data: [], total: 0 };
      }
    },
    actions: {
      async getSettings({ commit }) {
        try {
          commit(GET_SETTINGS.STARTED);
          const data = await httpClient.get('/api/reports-wizard/pipelines/filter-settings');
          commit(GET_SETTINGS.COMPLETED, data);
          return data;
        } catch (e) {
          commit(GET_SETTINGS.FAILED);
          logger.error('Failed to get settings', e);
          throw e;
        }
      },
      async scheduleManually({ commit }, { pipelineId, ids }) {
        try {
          commit(SCHEDULE_MANUALLY.STARTED);
          const data = await httpClient.post(`/api/reports-wizard/scheduler/schedule/${pipelineId}`, { ids });
          commit(SCHEDULE_MANUALLY.COMPLETED, data);
          return data;
        } catch (e) {
          commit(SCHEDULE_MANUALLY.FAILED);
          logger.error('Failed to schedule manually', e);
          throw e;
        }
      },
      async activate({ commit }, { pipelineId }) {
        try {
          commit(ACTIVATE.STARTED);
          const data = await httpClient.post(`/api/reports-wizard/scheduler/${pipelineId}/activate`);
          commit(ACTIVATE.COMPLETED, { pipelineId });
          return data;
        } catch (e) {
          commit(ACTIVATE.FAILED);
          logger.error('Failed to activate pipeline', e);
          throw e;
        }
      },
      async deactivate({ commit }, { pipelineId }) {
        try {
          commit(DEACTIVATE.STARTED);
          const data = await httpClient.post(`/api/reports-wizard/scheduler/${pipelineId}/deactivate`);
          commit(DEACTIVATE.COMPLETED, { pipelineId });
          return data;
        } catch (e) {
          commit(DEACTIVATE.FAILED);
          logger.error('Failed to deactivate pipeline', e);
          throw e;
        }
      },
      async fetchHistory({ commit }, { pipelineId, skip, limit, status, applicationId, startedFrom, startedTo }) {
        const requestToken = ++historyRequestToken;
        try {
          commit(FETCH_HISTORY.STARTED);
          const params = new URLSearchParams({
            pipelineId,
            size: limit,
            skip,
            ...(status ? { status } : {}),
            ...(applicationId ? { applicationId } : {}),
            ...(startedFrom ? { startedFrom } : {}),
            ...(startedTo ? { startedTo } : {})
          });
          const { data, total } = await httpClient.get(`/api/reports-wizard/pipeline-history?${params.toString()}`);
          if (requestToken !== historyRequestToken) {
            return undefined; // a newer request started after this one, ignore the stale response
          }
          commit(FETCH_HISTORY.COMPLETED, { data, total });
          return { data, total };
        } catch (e) {
          if (requestToken === historyRequestToken) {
            commit(FETCH_HISTORY.FAILED);
          }
          logger.error('Failed to fetch history', e);
          throw e;
        }
      }
    }
  })('pipelines', {
    async getById(id) {
      const pipeline = await httpClient.get(`/api/reports-wizard/pipelines/${id}`);
      return withId(pipeline);
    },
    async getCollection() {
      const [collection, infos] = await Promise.all([
        httpClient.get('/api/reports-wizard/pipelines'),
        httpClient.get('/api/reports-wizard/scheduler/scheduled-pipelines')
      ]);

      const data = collection
        .map(pipeline => {
          const info = infos.find(info => info.pipelineId === pipeline._id);
          return withId({
            ...pipeline,
            ...info
          });
        })
        .sort((a, b) => b.pending - a.pending);
      return { data, total: data.length };
    },
    async create(payload) {
      const pipeline = await httpClient.post('/api/reports-wizard/pipelines', payload);
      return withId(pipeline);
    },
    async update(id, args) {
      const pipeline = await httpClient.put(`/api/reports-wizard/pipelines/${id}`, args);
      return withId(pipeline);
    },
    async delete(id) {
      return httpClient.delete(`/api/reports-wizard/pipelines/${id}`);
    }
  })
};
